/**
 * App — route wiring for the ODE admin web shell (ODE-P01-T10 + ODE-P07-T01 +
 * ODE-P07-T02 + ODE-P07-T03 + ODE-P07-T04 + ODE-P07-T06 + ODE-P07-T07 +
 * ODE-P07-T08 + ODE-P07-T09).
 *
 *   /login                    staff login (real credential store)
 *   /                         → /modules/live-position
 *   /overview                 operational overview dashboard (ODE-P07-T01)
 *   /do-search                Delivery Order Search & Detail (ODE-P07-T03)
 *   /uang-jalan               Admin Uang Jalan Operational View (ODE-P07-T08)
 *   /journey-history          Journey History & Stop Points (ODE-P07-T06)
 *   /checkpoint-timeline      Checkpoint Timeline per Delivery Order (ODE-P07-T04)
 *   /reports                  Operational Reports & Export (ODE-P07-T09)
 *   /geofence-exceptions      Geofence Exceptions & Overrides (ODE-P07-T07)

 *   /evidence                 Evidence Viewer — checkpoint photo/evidence history (ODE-P07-T05)
 *   /map                      live tracking map (ODE-P07-T02)
 *   /modules/:moduleId        guarded module placeholder (REQ-043..048)
 *   *                         not found
 *
 * Guarded routes are wrapped in RequireAuth (session) + RequirePermission
 * (role mirror); the server-side T08 guard remains the authority. The
 * /overview, /map, /do-search, /uang-jalan, /journey-history and
 * /checkpoint-timeline pages additionally carry their own client mirror
 * (dashboard.view / tracking.view / uang_jalan.view / journey.view) and render
 * the full REQ-090 state set from the guarded read model. A catalog module
 * that has a real route (ODE-P07-T06) redirects from its
 * `/modules/:moduleId` placeholder to that route — no dead placeholder is left
 * reachable.
 *
 * Traces to: ODE-PRD-REQ-043..048, ODE-PRD-REQ-077, ODE-PRD-REQ-084,
 *            ODE-PRD-REQ-090.
 */

import { HashRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext.js';
import { CommunicationTraceDashboard } from './communication-trace/CommunicationTraceDashboard.js';
import { LoginScreen } from './components/LoginScreen.js';
import { ModulePlaceholder } from './components/ModulePlaceholder.js';
import { RequireAuth } from './components/RequireAuth.js';
import { RequirePermission } from './components/RequirePermission.js';
import { ShellLayout } from './components/ShellLayout.js';
import { OverviewPage } from './modules/overview/OverviewPage.js';
import type { OverviewDashboardSeams } from './modules/overview/OverviewDashboard.js';
import { DoSearchPage } from './modules/do-search/DoSearchPage.js';
import type { DoSearchSeams } from './modules/do-search/DoSearchModule.js';
import { UangJalanPage } from './modules/uang-jalan/UangJalanPage.js';
import type { UangJalanSeams } from './modules/uang-jalan/UangJalanModule.js';
import { JourneyHistoryPage } from './modules/journey-history/JourneyHistoryPage.js';
import type { JourneyHistorySeams } from './modules/journey-history/JourneyHistoryModule.js';
import { CheckpointTimelinePage } from './modules/checkpoint-timeline/CheckpointTimelinePage.js';
import type { CheckpointTimelineSeams } from './modules/checkpoint-timeline/CheckpointTimelineModule.js';
import { ReportsPage } from './modules/reports/ReportsPage.js';
import type { ReportsSeams } from './modules/reports/ReportsModule.js';
import { GeofenceExceptionsPage } from './modules/geofence-exceptions/GeofenceExceptionsPage.js';
import type { GeofenceExceptionsSeams } from './modules/geofence-exceptions/GeofenceExceptionsModule.js';

import { EvidenceViewerPage } from './modules/evidence-viewer/EvidenceViewerPage.js';
import type { EvidenceViewerSeams } from './modules/evidence-viewer/EvidenceViewerModule.js';
import { LiveMapPage } from './modules/live-map/LiveMapDashboard.js';
import type { LiveMapDashboardSeams } from './modules/live-map/LiveMapDashboard.js';
import { getModule } from './rbac/catalog.js';

function ModulePage(): JSX.Element {
  const { moduleId } = useParams();
  const module = getModule(moduleId ?? '');
  if (module === undefined) {
    return <NotFound />;
  }
  if (module.route !== undefined) {
    return <Navigate to={module.route} replace />;
  }
  return (
    <RequirePermission module={module}>
      {module.id === 'communication-trace' ? (
        <CommunicationTraceDashboard module={module} />
      ) : (
        <ModulePlaceholder module={module} />
      )}
    </RequirePermission>
  );
}

function NotFound(): JSX.Element {
  return (
    <section role="alert" data-testid="state-error" className="state state-error">
      <h3>Halaman tidak ditemukan</h3>
      <p>Modul yang diminta tidak dikenal.</p>
    </section>
  );
}

export interface AppProps {
  /** Test seam for the Overview dashboard (fetch + socket factories). */
  readonly overviewSeams?: OverviewDashboardSeams;
  /** Test seam for the Delivery Order Search & Detail module (fetch). */
  readonly doSearchSeams?: DoSearchSeams;
  /** Test seam for the Admin Uang Jalan Operational View module (fetch). */
  readonly uangJalanSeams?: UangJalanSeams;
  /** Test seam for the Journey History & Stop Points module (fetch). */
  readonly journeyHistorySeams?: JourneyHistorySeams;
  /** Test seam for the Checkpoint Timeline module (fetch). */
  readonly checkpointTimelineSeams?: CheckpointTimelineSeams;
  /** Test seam for the Operational Reports & Export module (fetch). */
  readonly reportsSeams?: ReportsSeams;
  /** Test seam for the Geofence Exceptions & Overrides module (fetch + write). */
  readonly geofenceExceptionsSeams?: GeofenceExceptionsSeams;

  /** Test seam for the Evidence Viewer module (fetch + clock). */
  readonly evidenceViewerSeams?: EvidenceViewerSeams;
  /** Test seam for the Live Tracking Map (fetch + socket + provider). */
  readonly liveMapSeams?: LiveMapDashboardSeams;
}

export default function App({
  overviewSeams,
  doSearchSeams,
  uangJalanSeams,
  journeyHistorySeams,
  checkpointTimelineSeams,
  reportsSeams,
  geofenceExceptionsSeams,

  evidenceViewerSeams,
  liveMapSeams,
}: AppProps = {}): JSX.Element {
  return (
    <AuthProvider>
      <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/login" element={<LoginScreen />} />
          <Route
            element={
              <RequireAuth>
                <ShellLayout />
              </RequireAuth>
            }
          >
            <Route path="/" element={<Navigate to="/modules/live-position" replace />} />
            <Route path="/overview" element={<OverviewPage seams={overviewSeams} />} />
            <Route path="/do-search" element={<DoSearchPage seams={doSearchSeams} />} />
            <Route path="/uang-jalan" element={<UangJalanPage seams={uangJalanSeams} />} />
            <Route
              path="/journey-history"
              element={<JourneyHistoryPage seams={journeyHistorySeams} />}
            />
            <Route
              path="/checkpoint-timeline"
              element={<CheckpointTimelinePage seams={checkpointTimelineSeams} />}
            />
            <Route path="/reports" element={<ReportsPage seams={reportsSeams} />} />
            <Route
              path="/geofence-exceptions"
              element={<GeofenceExceptionsPage seams={geofenceExceptionsSeams} />}
            />

            <Route path="/evidence" element={<EvidenceViewerPage seams={evidenceViewerSeams} />} />
            <Route path="/map" element={<LiveMapPage seams={liveMapSeams} />} />
            <Route path="/modules/:moduleId" element={<ModulePage />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </HashRouter>
    </AuthProvider>
  );
}
